import { useState, useEffect } from 'react';
import { useSuvSign } from '@/hooks/useSuvSign';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function SuvSignSettings() {
  const { config, loading, loadConfig, saveConfig, testConnection } = useSuvSign();
  const [formData, setFormData] = useState({
    api_url: '',
    api_key: '',
    webhook_secret: '',
    function_auth_token: '',
    is_active: true,
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (config) {
      setFormData({
        api_url: config.api_url || '',
        api_key: config.api_key || '',
        webhook_secret: config.webhook_secret || '',
        function_auth_token: config.function_auth_token || '',
        is_active: config.is_active,
      });
    }
  }, [config]);

  const handleTestConnection = async () => {
    setTesting(true);
    // Pass current form values to test (not saved config)
    await testConnection({ api_url: formData.api_url, api_key: formData.api_key });
    setTesting(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await saveConfig(formData);
    setSaving(false);
  };

  const webhookUrl = `https://irtygpadfbavrqhldhzm.supabase.co/functions/v1/suvsign-webhook`;

  if (loading && !config) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">SuvSign Integration</h1>
        <p className="text-muted-foreground">
          Configure your SuvSign e-signature integration
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>API Configuration</CardTitle>
          <CardDescription>
            Enter your SuvSign API credentials to enable e-signature functionality
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="api_url">API URL</Label>
            <Input
              id="api_url"
              value={formData.api_url}
              onChange={(e) => setFormData({ ...formData, api_url: e.target.value })}
              placeholder="https://vpysvlbfsvomwrgbpybc.supabase.co/functions/v1/api-handler"
            />
            <p className="text-xs text-muted-foreground">
              The base URL of your SuvSign API
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="api_key">API Key</Label>
            <Input
              id="api_key"
              type="password"
              value={formData.api_key}
              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
              placeholder="Enter your SuvSign API key"
            />
            <p className="text-xs text-muted-foreground">
              Your SuvSign API key for authentication
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="webhook_secret">Webhook Secret</Label>
            <Input
              id="webhook_secret"
              type="password"
              value={formData.webhook_secret}
              onChange={(e) => setFormData({ ...formData, webhook_secret: e.target.value })}
              placeholder="Enter webhook secret for signature validation"
            />
            <p className="text-xs text-muted-foreground">
              Used to validate webhook signatures from SuvSign
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="function_auth_token">Function Auth Token (Optional)</Label>
            <Input
              id="function_auth_token"
              type="password"
              value={formData.function_auth_token}
              onChange={(e) => setFormData({ ...formData, function_auth_token: e.target.value })}
              placeholder="JWT token if SuvSign edge function requires authentication"
            />
            <p className="text-xs text-muted-foreground">
              Required only if the SuvSign API endpoint is a protected Supabase edge function
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Enable SuvSign Integration</Label>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleTestConnection}
              variant="outline"
              disabled={testing || !formData.api_url || !formData.api_key}
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                'Test Connection'
              )}
            </Button>

            <Button
              onClick={handleSave}
              disabled={saving || !formData.api_url || !formData.api_key || !formData.webhook_secret}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Configuration'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Webhook Configuration</CardTitle>
          <CardDescription>
            Configure this webhook URL in your SuvSign dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Add this webhook URL to your SuvSign project to receive real-time updates when documents are signed, viewed, or completed.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>Webhook URL</Label>
            <div className="flex gap-2">
              <Input
                value={webhookUrl}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(webhookUrl);
                }}
              >
                Copy
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Events to Enable in SuvSign</Label>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>document.sent - When a document is sent for signature</li>
              <li>document.viewed - When a recipient views the document</li>
              <li>signatory.signed - When a signatory signs the document</li>
              <li>document.completed - When all signatures are collected</li>
              <li>document.declined - When a document is declined</li>
              <li>document.expired - When a document expires</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {config && (
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {config.is_active ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-600 font-medium">Integration Active</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  <span className="text-orange-600 font-medium">Integration Inactive</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
